/**
 * Client marketplace — browse photographers + videographers, view their
 * listings, and buy via Stripe Checkout. Purchased items appear in
 * "My Purchases" with clean-download links.
 *
 * Routes:
 *   /portal/buy/photo     → list of photographers
 *   /portal/buy/video     → list of videographers
 *   /portal/shooter/:uid  → a shooter's gallery with Buy buttons
 *   /portal/purchases     → my paid downloads
 */
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, BACKEND_URL, fmtDuration } from "@/lib/api";
import { PortalShell } from "@/pages/ClientPortalShell";
import { toast } from "sonner";
import { Camera, Video, ArrowRight, Download, Loader2, ShoppingBag, Users, Check } from "lucide-react";

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

export function ShooterList({ kind }) {
  // kind: "photo" | "video"
  const [shooters, setShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/marketplace/shooters?kind=${kind}`)
      .then((r) => setShooters(r.data.shooters || []))
      .finally(() => setLoading(false));
  }, [kind]);
  const Icon = kind === "photo" ? Camera : Video;
  const title = kind === "photo" ? "Buy a Photo" : "Buy a Video";
  const noun = kind === "photo" ? "photographer" : "videographer";

  return (
    <PortalShell>
      <div className="border-l-4 border-sky-600 pl-5 mb-8">
        <div className="label-tiny mb-2 inline-flex items-center gap-2">
          <Icon size={12} /> Marketplace
        </div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">{title}</h1>
        <p className="text-stone-400 mt-3">
          Browse {noun}s covering rodeos across our associations. Click a {noun} to see their gallery and prices.
        </p>
      </div>

      {loading ? (
        <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
          <Loader2 size={13} className="animate-spin" /> Loading {noun}s…
        </div>
      ) : shooters.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-14 text-center text-stone-500" data-testid={`marketplace-empty-${kind}`}>
          No {noun}s have listed items yet. Check back soon.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`marketplace-shooters-${kind}`}>
          {shooters.map((s) => (
            <Link
              key={s.id}
              to={`/portal/shooter/${s.id}?kind=${kind}`}
              className="border border-stone-800 hover:border-sky-700 bg-stone-950 p-4 group transition-colors"
              data-testid={`marketplace-shooter-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display uppercase font-bold text-lg truncate group-hover:text-sky-400">
                    {s.name || s.email}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-1">
                    {s.item_count} listing{s.item_count === 1 ? "" : "s"} · {kind === "photo" ? "Photos" : "Videos"}
                  </div>
                </div>
                <ArrowRight size={14} className="text-stone-500 group-hover:text-sky-500 mt-1 shrink-0" />
              </div>
              {s.watermark_text && (
                <div className="mt-3 text-[10px] uppercase tracking-widest text-stone-500 truncate">
                  {s.watermark_text}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </PortalShell>
  );
}

export function ShooterGallery() {
  const { uid } = useParams();
  const [params] = useSearchParams();
  const kindHint = params.get("kind") || "photo";
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [ownedIds, setOwnedIds] = useState(new Set());

  useEffect(() => {
    api.get(`/marketplace/shooters/${uid}`).then((r) => setData(r.data)).catch(() => setData({ shooter: null }));
    api.get(`/marketplace/me/purchases`).then((r) => {
      const s = new Set((r.data.purchases || []).map((p) => p.item?.id).filter(Boolean));
      setOwnedIds(s);
    }).catch(() => {});
  }, [uid]);

  const buy = async (it) => {
    setBusyId(it.id);
    try {
      const success = `${window.location.origin}/portal/purchases`;
      const cancel = window.location.href;
      const r = await api.post(`/marketplace/items/${it.id}/checkout`, { success_url: success, cancel_url: cancel });
      if (r.data.already_purchased) {
        toast.success("You already own this — see My Purchases");
        window.location.href = "/portal/purchases";
        return;
      }
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error("Checkout failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    return <PortalShell><div className="text-stone-500 text-sm">Loading gallery…</div></PortalShell>;
  }
  if (!data.shooter) {
    return <PortalShell><div className="text-stone-400">Not found.</div></PortalShell>;
  }
  const shooter = data.shooter;
  const items = data.items || [];
  const isPhotoRole = shooter.role === "photographer";
  const Icon = isPhotoRole ? Camera : Video;

  return (
    <PortalShell>
      <div className="mb-4">
        <Link to={`/portal/buy/${kindHint}`} className="text-[11px] uppercase tracking-widest text-stone-500 hover:text-sky-400 inline-flex items-center gap-1">
          ← Back to all {kindHint === "photo" ? "photographers" : "videographers"}
        </Link>
      </div>
      <div className="border-l-4 border-sky-600 pl-5 mb-8">
        <div className="label-tiny mb-2 inline-flex items-center gap-2">
          <Icon size={12} /> {isPhotoRole ? "Photographer" : "Videographer"}
        </div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">{shooter.name || shooter.email}</h1>
        {shooter.watermark_text && (
          <p className="text-stone-500 mt-3 text-sm">{shooter.watermark_text}</p>
        )}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-14 text-center text-stone-500">
          No listings yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="marketplace-gallery">
          {items.map((it) => {
            const owned = ownedIds.has(it.id);
            const isPhoto = it.kind === "photo";
            const src = `${BACKEND_URL}/api/marketplace/items/${it.id}/preview`;
            return (
              <div key={it.id} className="border border-stone-800 hover:border-sky-700 transition-colors" data-testid={`marketplace-item-${it.id}`}>
                <div className="aspect-video bg-black relative overflow-hidden">
                  {isPhoto ? (
                    <img src={src} alt={it.title} className="w-full h-full object-cover" />
                  ) : (
                    <video
                      src={src}
                      controls
                      controlsList="nodownload"
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-contain bg-black"
                      data-testid={`marketplace-item-video-${it.id}`}
                    />
                  )}
                  {shooter.watermark_text && !owned && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="font-display text-white/70 text-3xl uppercase tracking-widest rotate-[-25deg] mix-blend-difference select-none text-center px-4">
                        {shooter.watermark_text}
                      </div>
                    </div>
                  )}
                  {it.duration > 0 && (
                    <div className="absolute bottom-1.5 right-1.5 font-mono-tc text-[10px] bg-black/70 px-1.5 py-0.5">
                      {fmtDuration(it.duration)}
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-display uppercase font-bold truncate mb-1">{it.title || "Untitled"}</div>
                  {it.description && (
                    <div className="text-[11px] text-stone-500 truncate mb-2">{it.description}</div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="font-mono-tc text-lg font-bold text-orange-400">{dollars(it.price_cents || 0)}</div>
                    {owned ? (
                      <Link to="/portal/purchases" className="ml-auto btn-secondary !py-1 text-xs inline-flex items-center gap-1 !text-emerald-400">
                        <Check size={11} /> Owned
                      </Link>
                    ) : (
                      <button
                        onClick={() => buy(it)}
                        disabled={busyId === it.id}
                        className="ml-auto btn-primary !py-1 !px-3 text-xs inline-flex items-center gap-1"
                        data-testid={`marketplace-buy-${it.id}`}
                      >
                        {busyId === it.id ? <Loader2 size={11} className="animate-spin" /> : <ShoppingBag size={11} />}
                        Buy
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PortalShell>
  );
}

export function MyPurchases() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/marketplace/me/purchases").then((r) => setPurchases(r.data.purchases || [])).finally(() => setLoading(false));
    // If Stripe just redirected us with a session_id, show a toast.
    if (params.get("session_id")) {
      toast.success("Thanks for the purchase! Downloads unlock as Stripe confirms — refresh in a few seconds if it isn't listed yet.");
    }
  }, [params, nav]);

  return (
    <PortalShell>
      <div className="border-l-4 border-sky-600 pl-5 mb-8">
        <div className="label-tiny mb-2 inline-flex items-center gap-2"><ShoppingBag size={12} /> Marketplace</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">My Purchases</h1>
      </div>

      {loading ? (
        <div className="text-stone-500 inline-flex items-center gap-2 text-sm">
          <Loader2 size={13} className="animate-spin" /> Loading purchases…
        </div>
      ) : purchases.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-14 text-center text-stone-500" data-testid="purchases-empty">
          <p className="mb-3">No purchases yet.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/portal/buy/photo" className="btn-secondary text-sm inline-flex items-center gap-2"><Camera size={12} /> Buy a Photo</Link>
            <Link to="/portal/buy/video" className="btn-secondary text-sm inline-flex items-center gap-2"><Video size={12} /> Buy a Video</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="purchases-grid">
          {purchases.map((p) => (
            <div key={p.id} className="border border-stone-800" data-testid={`purchase-${p.id}`}>
              <div className="p-3">
                <div className="font-display uppercase font-bold truncate">{p.item?.title || "Purchase"}</div>
                <div className="text-[11px] text-stone-500 mt-1">
                  From {p.shooter?.name || "shooter"} · {dollars(p.amount_cents || 0)}
                </div>
                <a
                  href={`${BACKEND_URL}/api/marketplace/purchases/${p.id}/download`}
                  className="btn-primary text-xs mt-3 inline-flex items-center gap-2"
                  data-testid={`purchase-download-${p.id}`}
                >
                  <Download size={12} /> Download {p.item?.kind === "photo" ? "photo" : "video"}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </PortalShell>
  );
}

// Component embedded on ClientMyClips to expose the two "Buy" entry points.
export function BuyButtons() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="portal-buy-buttons">
      <Link
        to="/portal/buy/photo"
        className="border border-sky-900/40 bg-sky-950/10 hover:bg-sky-950/30 hover:border-sky-700 transition-colors p-4 group flex items-center gap-3"
        data-testid="portal-buy-photo"
      >
        <div className="w-10 h-10 flex items-center justify-center border border-sky-800/60 group-hover:border-sky-600">
          <Camera size={16} className="text-sky-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display uppercase font-bold text-base group-hover:text-sky-400">Buy a Photo</div>
          <div className="text-[11px] text-stone-500">Browse photographers &amp; their galleries</div>
        </div>
        <ArrowRight size={14} className="text-stone-500 group-hover:text-sky-500" />
      </Link>
      <Link
        to="/portal/buy/video"
        className="border border-sky-900/40 bg-sky-950/10 hover:bg-sky-950/30 hover:border-sky-700 transition-colors p-4 group flex items-center gap-3"
        data-testid="portal-buy-video"
      >
        <div className="w-10 h-10 flex items-center justify-center border border-sky-800/60 group-hover:border-sky-600">
          <Video size={16} className="text-sky-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display uppercase font-bold text-base group-hover:text-sky-400">Buy a Video</div>
          <div className="text-[11px] text-stone-500">Browse videographers &amp; their reels</div>
        </div>
        <ArrowRight size={14} className="text-stone-500 group-hover:text-sky-500" />
      </Link>
    </div>
  );
}
